import { Box, Flex, FormLabel, NumberInput, NumberInputField, Switch, Text } from '@chakra-ui/react';
import { percentageFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import SelectInput from 'components/inputs/SelectInput';
import { generateSelectOptions } from 'helpers/inputHelper';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { mapRoasThresholdsFromApi } from 'pages/Cockpit/Operations/roasCovenantMonth';
import SalesSectionCard from '../Sales/components/SalesSectionCard';
import { cohortColor } from './cohortColor';
import { FIT_TYPE, FIT_TYPE_META } from './forecast/fitCurve';
import { ROAS_HORIZON, forecastRoas } from './forecast/roasExAnteForecast';

const COVENANT_COLOR = '#E53E3E';
const ONE_SIGMA_FILL = 'rgba(147, 197, 253, 0.22)';
const TWO_SIGMA_FILL = 'rgba(191, 219, 254, 0.14)';

export function roasSigmaBandFill(k) {
  return k === 1 ? ONE_SIGMA_FILL : TWO_SIGMA_FILL;
}

export function covenantMarkerPoints(thresholds) {
  return (thresholds || []).map((threshold) => [threshold.month, threshold.minRoasPct]);
}

export function roasCurveScale(percent) {
  if (percent === '' || percent == null) {
    return 1;
  }
  const value = Number(percent);
  if (!Number.isFinite(value) || value < 0) {
    return 1;
  }
  return value / 100;
}

export function roasSigmaBandSeries(band, scale) {
  return (band || [])
    .map((point, mob) =>
      point && Number.isFinite(point.lower) && Number.isFinite(point.upper)
        ? [mob, [point.lower * scale, point.upper * scale]]
        : null
    )
    .filter(Boolean);
}

export function scaledCohortRoas(cohort, scale) {
  const actual = (cohort.roas || [])
    .map((value, mob) => (value == null ? null : [mob, value * scale]))
    .filter(Boolean);
  const prediction = (cohort.predictedRoas || []).map(([mob, value]) => [
    mob,
    value * scale,
  ]);
  const forecast = (cohort.exante || []).map((value, mob) => [mob, value * scale]);
  return { actual, prediction, forecast };
}

export function thresholdStep(thresholds, scale) {
  const sorted = [...thresholds].sort((left, right) => left.month - right.month);
  const points = [];
  sorted.forEach((threshold, index) => {
    const y = threshold.minRoasPct * scale;
    if (!Number.isFinite(y)) {
      return;
    }
    const end = sorted[index + 1] ? sorted[index + 1].month : ROAS_HORIZON - 1;
    points.push([threshold.month, y]);
    points.push([end, y]);
  });
  return points;
}

const RoasByMobChart = ({ cohorts, selectedIds, deal }) => {
  const [fitType, setFitType] = useState(FIT_TYPE.LINEAR);
  const [roasScalePercent, setRoasScalePercent] = useState('100');
  const [showAnchors, setShowAnchors] = useState(true);
  const [showForecast, setShowForecast] = useState(true);
  const [showBands, setShowBands] = useState(false);

  const forecast = useMemo(
    () => forecastRoas(cohorts, { fitType }),
    [cohorts, fitType]
  );
  const selected = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const curveScale = roasCurveScale(roasScalePercent);

  const chart = useMemo(() => {
    const bandSeries = [];
    const lineSeries = [];
    let withForecast = 0;
    forecast.cohorts.forEach((cohort, index) => {
      if (!selected.has(cohort.id)) {
        return;
      }
      const color = cohortColor(index);
      if (cohort.exante) {
        withForecast += 1;
      }
      const scaled = scaledCohortRoas(cohort, curveScale);
      if (scaled.actual.length) {
        lineSeries.push({
          name: `${cohort.label} actual`,
          type: 'line',
          color,
          data: scaled.actual,
          showMarkers: true,
        });
      }
      if (scaled.prediction.length) {
        lineSeries.push({
          name: `${cohort.label} prediction`,
          type: 'line',
          color,
          data: scaled.prediction,
          showMarkers: true,
          hollow: true,
          dashArray: 2,
        });
      }
      if (showForecast && cohort.exante) {
        if (showBands && cohort.bands) {
          [2, 1].forEach((k) => {
            const data = roasSigmaBandSeries(cohort.bands[k], curveScale);
            if (!data.length) {
              return;
            }
            bandSeries.push({
              name: `${cohort.label} ±${k}σ`,
              type: 'rangeArea',
              color: roasSigmaBandFill(k),
              fillOpacity: k === 1 ? 0.22 : 0.14,
              band: true,
              bandK: k,
              data,
            });
          });
        }
        lineSeries.push({
          name: `${cohort.label} forecast`,
          type: 'line',
          color,
          data: scaled.forecast,
          showMarkers: true,
          hollow: true,
          dashArray: 6,
        });
      }
    });
    const series = [...bandSeries, ...lineSeries];

    const thresholds = mapRoasThresholdsFromApi(deal?.roasThresholds).filter(
      (threshold) =>
        Number.isFinite(Number(threshold.month)) &&
        Number(threshold.minRoasPct) > 0
    ).map((threshold) => ({
      month: Number(threshold.month),
      minRoasPct: Number(threshold.minRoasPct),
    }));

    if (showAnchors && thresholds.length) {
      series.push({
        name: 'ROAS covenant',
        type: 'line',
        color: COVENANT_COLOR,
        data: thresholdStep(thresholds, 1),
        dashArray: 6,
        strokeWidth: 3,
      });
      series.push({
        name: 'ROAS covenant',
        type: 'scatter',
        color: COVENANT_COLOR,
        data: covenantMarkerPoints(thresholds),
        showMarkers: true,
        markerShape: 'diamond',
        markerSize: 7,
        markerStrokeWidth: 0,
        strokeWidth: 0,
        covenantMarker: true,
      });
      if (Number(deal?.secondLevelBreachPct) > 0) {
        series.push({
          name: '2nd-level breach',
          type: 'line',
          color: '#DD6B20',
          data: thresholdStep(thresholds, Number(deal.secondLevelBreachPct)),
          dashArray: 2,
          strokeWidth: 2,
        });
      }
    }

    return { series, withForecast };
  }, [forecast, selected, curveScale, showForecast, showBands, showAnchors, deal]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'line',
        fontFamily: 'DM Sans',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: chart.series.map((item) => item.color),
      stroke: {
        width: chart.series.map((item) => {
          if (item.type === 'rangeArea') {
            return 0;
          }
          return item.strokeWidth ?? 2;
        }),
        dashArray: chart.series.map((item) => item.dashArray || 0),
        curve: 'straight',
      },
      fill: {
        opacity: chart.series.map((item) => item.fillOpacity ?? 1),
      },
      markers: {
        size: chart.series.map((item) => item.markerSize ?? (item.showMarkers ? 4 : 0)),
        shape: chart.series.map((item) => item.markerShape || 'circle'),
        colors: chart.series.map((item) => (item.hollow ? '#ffffff' : item.color)),
        strokeColors: chart.series.map((item) => item.color),
        strokeWidth: chart.series.map((item) => item.markerStrokeWidth ?? 2),
        hover: { sizeOffset: 2 },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      tooltip: {
        shared: false,
        intersect: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const pointSeries = w.config.series[seriesIndex];
          const point = pointSeries?.data?.[dataPointIndex];
          if (!Array.isArray(point)) {
            return '';
          }
          const items = pointSeries.covenantMarker
            ? [
                { label: 'Month', value: `M${point[0]}` },
                {
                  label: 'ROAS',
                  value: percentageFormat(point[1], { maximumFractionDigits: 0 }),
                },
              ]
            : Array.isArray(point[1])
              ? [
                  { label: 'MOB', value: point[0] },
                  {
                    label: 'ROAS',
                    value: `${percentageFormat(point[1][0], {
                      maximumFractionDigits: 1,
                    })} – ${percentageFormat(point[1][1], {
                      maximumFractionDigits: 1,
                    })}`,
                  },
                ]
              : [
                  { label: 'MOB', value: point[0] },
                  {
                    label: 'ROAS',
                    value: percentageFormat(point[1], { maximumFractionDigits: 1 }),
                  },
                ];
          return renderToString(
            <ChartTooltip title={pointSeries.name} hideMarkers items={items} />
          );
        },
      },
      xaxis: {
        type: 'numeric',
        min: 0,
        max: ROAS_HORIZON - 1,
        tickAmount: 12,
        title: { text: 'Month on book' },
        labels: { formatter: (value) => String(Math.round(value)) },
        tooltip: { enabled: false },
      },
      yaxis: {
        title: { text: 'Cumulative ROAS' },
        labels: {
          formatter: (value) => percentageFormat(value, { maximumFractionDigits: 0 }),
        },
      },
    }),
    [chart.series]
  );

  return (
    <SalesSectionCard title="Cumulative ROAS by month on book">
      <Flex gap={4} wrap="wrap" align="center" mt={4}>
        <Flex align="center" gap={2}>
          <FormLabel m={0}>M1 fit</FormLabel>
          <SelectInput
            options={generateSelectOptions({ metaObj: FIT_TYPE_META })}
            value={fitType}
            onChange={setFitType}
            w="160px"
          />
        </Flex>
        <Flex align="center" gap={2}>
          <FormLabel m={0}>Scale</FormLabel>
          <NumberInput
            value={roasScalePercent}
            onChange={(value) => setRoasScalePercent(value)}
            min={0}
            w="90px"
          >
            <NumberInputField placeholder="100" />
          </NumberInput>
          <Text fontSize="sm">%</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Switch
            isChecked={showAnchors}
            onChange={(event) => setShowAnchors(event.target.checked)}
          />
          <Text fontSize="sm">Deal anchors</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Switch
            isChecked={showForecast}
            onChange={(event) => setShowForecast(event.target.checked)}
          />
          <Text fontSize="sm">Ex-ante forecast</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Switch
            isChecked={showBands}
            onChange={(event) => setShowBands(event.target.checked)}
          />
          <Text fontSize="sm">±1σ / ±2σ bands</Text>
        </Flex>
      </Flex>
      <Text mt={3} fontSize="sm" color="gray.600">
        Solid = actual · dotted = cohort prediction · dashed = ex-ante forecast. MOB 0 is
        the first cohort month. {FIT_TYPE_META[fitType].label} fit · {chart.withForecast}{' '}
        selected{' '}
        {chart.withForecast === 1 ? 'cohort has' : 'cohorts have'} an ex-ante curve
      </Text>
      <Box h="420px" mt={2}>
        {chart.series.length ? (
          <Chart type="line" width="100%" height="100%" series={chart.series} options={options} />
        ) : (
          <Text color="gray.500" mt={8}>
            Select a cohort to plot ROAS.
          </Text>
        )}
      </Box>
    </SalesSectionCard>
  );
};

RoasByMobChart.propTypes = {
  cohorts: PropTypes.array,
  selectedIds: PropTypes.array,
  deal: PropTypes.object,
};

export default RoasByMobChart;
